import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Search, Sparkles, Loader2, Check, X } from "lucide-react";

/**
 * HSN / SAC intelligence picker for a quote line (or product).  Supports:
 *   - live keyword/code search against the local hsn_master
 *   - one-tap AI-assisted suggestion from the product name/description
 *     (human still verifies before applying)
 */
export function HsnPicker({ value, productName, productDesc, type = "HSN", onChange, testid = "hsn" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggest, setSuggest] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/master/hsn/search`, { params: { q: term, limit: 20, type } });
        setResults(data.items || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open, type]);

  const runSuggest = async () => {
    if (!(productName || "").trim()) { toast.error("Add a product name first"); return; }
    setSuggesting(true);
    setOpen(true);
    try {
      const { data } = await api.post(`/master/hsn/suggest`, {
        name: productName, description: productDesc || "", type,
      });
      setSuggest(data);
      if (!(data.ai_suggestion) && !(data.keyword_matches || []).length)
        toast.info("No matching HSN found — try searching manually.");
    } catch {
      toast.error("Suggestion failed");
    } finally { setSuggesting(false); }
  };

  const pick = (item) => {
    onChange(item.code, item);
    setOpen(false);
    setSuggest(null);
    setQ("");
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <Search className="h-3 w-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            className="h-8 text-[12px] pl-7 pr-6"
            value={open ? q : (value || "")}
            placeholder={value ? value : "HSN / SAC"}
            onFocus={() => { setOpen(true); setQ(""); }}
            onChange={(e) => setQ(e.target.value)}
            data-testid={`${testid}-input`} />
          {value && !open && (
            <button onClick={() => onChange("", null)} type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600"
              data-testid={`${testid}-clear`} aria-label="Clear HSN">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <button type="button" onClick={runSuggest} disabled={suggesting}
          title="AI-suggest an HSN from the product name"
          className="h-8 px-2 rounded-md border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 flex items-center gap-1 text-[11px] font-medium disabled:opacity-50"
          data-testid={`${testid}-suggest-btn`}>
          {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
        </button>
      </div>

      {open && (
        <div className="absolute z-[70] mt-1 w-[380px] max-w-[85vw] bg-white border border-slate-200 rounded-lg shadow-xl p-2 max-h-80 overflow-y-auto"
          data-testid={`${testid}-dropdown`}>
          {suggest?.ai_suggestion && (
            <div className="mb-2 rounded-md border border-violet-200 bg-violet-50/60 p-2"
              data-testid={`${testid}-ai-card`}>
              <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-violet-700 mb-1">
                <Sparkles className="h-3 w-3" /> AI suggestion
                <span className="ml-auto text-violet-600">{suggest.ai_suggestion.confidence}% match</span>
              </div>
              <div className="font-mono text-[13px] font-semibold text-slate-900">
                {suggest.ai_suggestion.code_display}
                <span className="ml-1 text-[9px] font-sans font-normal px-1 rounded bg-slate-200 text-slate-600">{suggest.ai_suggestion.type}</span>
              </div>
              <div className="text-[11px] text-slate-600 leading-snug">{suggest.ai_suggestion.description}</div>
              {suggest.ai_suggestion.reason && (
                <div className="text-[10px] text-slate-500 italic mt-0.5">Why: {suggest.ai_suggestion.reason}</div>
              )}
              <button type="button" onClick={() => pick(suggest.ai_suggestion)}
                className="mt-1.5 w-full h-7 rounded bg-violet-700 hover:bg-violet-800 text-white text-[11px] font-medium flex items-center justify-center gap-1"
                data-testid={`${testid}-ai-use`}>
                <Check className="h-3 w-3" /> Verify &amp; use this code
              </button>
            </div>
          )}

          {(suggest?.keyword_matches?.length > 0 && !q) && (
            <div className="text-[9px] uppercase tracking-wider text-slate-400 px-1 mb-1">Related codes</div>
          )}
          {loading && <div className="p-2 text-[11px] text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</div>}
          {(q ? results : (suggest?.keyword_matches || [])).map((it) => (
            <button key={it.code} type="button" onClick={() => pick(it)}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-start gap-2"
              data-testid={`${testid}-opt-${it.code}`}>
              <span className="font-mono text-[12px] font-semibold text-slate-800 min-w-[64px]">{it.code_display}</span>
              <span className="flex-1 text-[11px] text-slate-600 leading-snug">{it.description}</span>
              <span className="text-[9px] px-1 rounded bg-slate-100 text-slate-500">{it.type}</span>
            </button>
          ))}
          {q && !loading && results.length === 0 && (
            <div className="p-2 text-[11px] text-slate-400">No codes match “{q}”.</div>
          )}
          {!q && !suggest && (
            <div className="p-2 text-[11px] text-slate-400">Type a code or keyword, or tap the ✦ to AI-suggest.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default HsnPicker;
